import type { ApiErrorBody, ErrorCode, FeatureKey, LimitKey } from '@dezza/shared';
import { API_ORIGIN, clientType, tokenStore } from './platform';

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode | 'NETWORK';
  readonly details?: unknown;
  readonly feature?: FeatureKey;
  readonly limit?: LimitKey;
  readonly requestId?: string;

  constructor(status: number, body: Partial<ApiErrorBody['error']> & { message: string }) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = (body.code ?? 'INTERNAL') as ErrorCode;
    this.details = body.details;
    this.feature = body.feature as FeatureKey | undefined;
    this.limit = body.limit as LimitKey | undefined;
    this.requestId = body.requestId;
  }

  /** Erros de campo ({ campo: mensagem }) para exibir nos formulários. */
  get fields(): Record<string, string> {
    const d = this.details as { fields?: Record<string, string> } | undefined;
    return d?.fields ?? {};
  }
}

/** Eventos globais disparados pelo cliente HTTP (tratados no AppShell/AuthProvider). */
export const apiEvents = new EventTarget();
export type ApiEventName = 'unauthenticated' | 'upgrade' | 'reauth' | 'suspended';

function emit(name: ApiEventName, detail?: unknown) {
  apiEvents.dispatchEvent(new CustomEvent(name, { detail }));
}

type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Não disparar eventos globais (ex.: /auth/me no boot). */
  silent?: boolean;
}

function buildUrl(path: string, query?: Query): string {
  const url = `${API_ORIGIN}/api${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `${url}?${s}` : url;
}

async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    'x-requested-with': 'fetch',
    'x-client-type': clientType,
    ...opts.headers,
  };
  const token = tokenStore.get();
  if (token) headers.authorization = `Bearer ${token}`;
  let body: BodyInit | undefined;
  if (opts.body instanceof FormData) body = opts.body;
  else if (opts.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), { method, headers, body, credentials: 'include', signal: opts.signal });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError(0, { code: 'INTERNAL', message: 'Sem conexão com o servidor. Verifique sua internet.' });
  }

  if (res.status === 204 || res.status === 304) return undefined as T;
  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const errBody = (data as ApiErrorBody | undefined)?.error ?? { code: 'INTERNAL' as ErrorCode, message: 'Erro inesperado. Tente novamente.' };
    const error = new ApiError(res.status, errBody);
    if (!opts.silent) {
      if (error.code === 'UNAUTHENTICATED' || error.code === 'SESSION_EXPIRED') emit('unauthenticated');
      else if (error.code === 'FEATURE_NOT_AVAILABLE' || error.code === 'LIMIT_REACHED') emit('upgrade', { feature: error.feature, limit: error.limit, message: error.message });
      else if (error.code === 'COMPANY_SUSPENDED') emit('suspended', { message: error.message });
    }
    throw error;
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, query?: Query, opts?: Omit<RequestOptions, 'query'>) => request<T>('GET', path, { ...opts, query }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PATCH', path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PUT', path, { ...opts, body }),
  del: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, opts),

  /** Envio de arquivo (multipart). */
  upload: <T>(path: string, file: Blob, fieldName = 'file', query?: Query) => {
    const form = new FormData();
    form.append(fieldName, file, file instanceof File ? file.name : 'upload.jpg');
    return request<T>('POST', path, { body: form, query });
  },

  /** Baixa um arquivo (exportações) e dispara o download no navegador. */
  async download(path: string, query?: Query, fallbackName = 'arquivo'): Promise<void> {
    const headers: Record<string, string> = { 'x-requested-with': 'fetch', 'x-client-type': clientType };
    const token = tokenStore.get();
    if (token) headers.authorization = `Bearer ${token}`;
    const res = await fetch(buildUrl(path, query), { headers, credentials: 'include' });
    if (!res.ok) {
      let msg = 'Não foi possível gerar o arquivo.';
      try {
        const j = (await res.json()) as ApiErrorBody;
        msg = j.error.message;
        if (j.error.code === 'FEATURE_NOT_AVAILABLE' || j.error.code === 'LIMIT_REACHED') emit('upgrade', { feature: j.error.feature, message: msg });
        throw new ApiError(res.status, j.error);
      } catch (e) {
        if (e instanceof ApiError) throw e;
        throw new ApiError(res.status, { message: msg });
      }
    }
    const blob = await res.blob();
    const cd = res.headers.get('content-disposition') ?? '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    const name = match ? decodeURIComponent(match[1]!) : fallbackName;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5_000);
  },
};

/** Mensagem amigável para qualquer erro. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Algo deu errado. Tente novamente.';
}
